import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MarkerType } from "reactflow";
import "reactflow/dist/style.css";
import AgentNode from "./AgentNode";

const nodeTypes = { agentNode: AgentNode };

const AGENT_NODE_ID = "agent-root";
const NODE_HEIGHT = 50;
const NODE_V_GAP = 30;
const NODE_H_GAP = 120;

// Calculate how tall a level-1 item needs (based on its children or itself)
function getItemSpan(item) {
  if (item.type === "sub_agent" && item.tool.toolData?.tools) {
    const childCount = Object.keys(item.tool.toolData.tools).length;
    if (childCount > 1) {
      return childCount * NODE_HEIGHT + (childCount - 1) * NODE_V_GAP;
    }
  }
  return NODE_HEIGHT;
}

function buildFlowGraph(promptConfig, tools, highlightedNodeId) {
  if (!promptConfig) return { nodes: [], edges: [] };

  const nodes = [];
  const edges = [];

  const subAgents = tools.filter((t) => t.type === "sub_agent");
  const regularTools = tools.filter((t) => t.type !== "sub_agent");

  // Collect all second-level children (sub-agents first, then regular tools)
  const level1Items = [];

  subAgents.forEach((sa) => {
    level1Items.push({ id: `sa-${sa.name || sa.id}`, type: "sub_agent", label: sa.name, tool: sa });
  });

  regularTools.forEach((t) => {
    level1Items.push({ id: `tool-${t.name || t.id}`, type: "tool", label: t.name, tool: t });
  });

  // Calculate vertical span for each item and total height
  const spans = level1Items.map(getItemSpan);
  const totalHeight = spans.reduce((sum, s) => sum + s, 0) + (level1Items.length - 1) * NODE_V_GAP;

  // Agent root node — on the left, vertically centered
  nodes.push({
    id: AGENT_NODE_ID,
    type: "agentNode",
    position: { x: 0, y: 0 },
    data: { label: "Agent", nodeType: "agent", highlighted: highlightedNodeId === AGENT_NODE_ID },
    draggable: false,
    selectable: false,
  });

  const LEVEL1_X = NODE_H_GAP + 160;
  const LEVEL2_X = LEVEL1_X + NODE_H_GAP + 160;

  // Place each level-1 item vertically, advancing cursor by its span
  let cursorY = -totalHeight / 2;

  level1Items.forEach((item, i) => {
    const span = spans[i];
    // Center the node within its span
    const y = cursorY + span / 2 - NODE_HEIGHT / 2;

    nodes.push({
      id: item.id,
      type: "agentNode",
      position: { x: LEVEL1_X, y },
      data: { label: item.label, nodeType: item.type, highlighted: highlightedNodeId === item.id },
      draggable: false,
      selectable: false,
    });

    edges.push({
      id: `e-${AGENT_NODE_ID}-${item.id}`,
      source: AGENT_NODE_ID,
      target: item.id,
      type: "default",
      markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12 },
      style: { stroke: "#94a3b8", strokeWidth: 1.5 },
    });

    // If sub_agent, lay out child tools vertically within this item's span
    if (item.type === "sub_agent" && item.tool.toolData?.tools) {
      const childTools = Object.values(item.tool.toolData.tools);
      const childTotalHeight = childTools.length * NODE_HEIGHT + (childTools.length - 1) * NODE_V_GAP;
      const childStartY = cursorY + span / 2 - childTotalHeight / 2;

      childTools.forEach((ct, ci) => {
        const childId = `tool-${item.tool.name}-${ct.name || ci}`;
        const cy = childStartY + ci * (NODE_HEIGHT + NODE_V_GAP);

        nodes.push({
          id: childId,
          type: "agentNode",
          position: { x: LEVEL2_X, y: cy },
          data: { label: ct.name, nodeType: "tool", highlighted: highlightedNodeId === childId },
          draggable: false,
          selectable: false,
        });

        edges.push({
          id: `e-${item.id}-${childId}`,
          source: item.id,
          target: childId,
          type: "default", // smoothstep
          markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12 },
          style: { stroke: "#94a3b8", strokeWidth: 1.5 },
        });
      });
    }

    cursorY += span + NODE_V_GAP;
  });

  return { nodes, edges };
}

export function getHighlightNodeId(event) {
  if (!event) return null;
  const { type, payload, isSubAgent } = event;

  if (type === "sub_agent_start" || type === "sub_agent_end") {
    return `sa-${payload?.subAgentName}`;
  }
  if (type === "tool_start" || type === "tool_end") {
    if (isSubAgent) {
      // Tool inside a sub-agent — we'd need parent context; highlight the tool generically
      return null; // handled separately in AgentMonitor
    }
    return `tool-${payload?.toolName}`;
  }
  if (type === "llm_request" || type === "llm_response") {
    if (isSubAgent) return null; // sub-agent LLM call
    return AGENT_NODE_ID;
  }
  return null;
}

export function getHighlightNodeIdForSubAgentTool(event, tools) {
  if (!event || !event.isSubAgent) return null;
  const { type, payload } = event;

  if (type === "tool_start" || type === "tool_end") {
    // Find which sub-agent owns this tool
    const subAgents = tools.filter((t) => t.type === "sub_agent");
    for (const sa of subAgents) {
      if (sa.toolData?.tools) {
        const childTools = Object.values(sa.toolData.tools);
        const match = childTools.find((ct) => ct.name === payload?.toolName);
        if (match) {
          return `tool-${sa.name}-${match.name}`;
        }
      }
    }
  }
  return null;
}

export default function AgentView({ promptConfig, tools, highlightedNodeId }) {
  const { nodes, edges } = useMemo(
    () => buildFlowGraph(promptConfig, tools, highlightedNodeId),
    [promptConfig, tools, highlightedNodeId]
  );

  return (
    <div className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={true}
        zoomOnScroll={true}
        preventScrolling={true}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#e2e8f0" gap={20} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
